import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Combobox } from '@/components/Combobox/Combobox'
import type { Breed } from '@/services/PriceCalculator/Field.types'
import { UNKNOWN_BREED_ID } from '@/services/PriceCalculator/Field.types'
import { Text } from 'ui/src/components/Text/Text'
import { CrossIconSmall } from 'ui/src/icons/CrossIconSmall'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { combobox, content, deleteButton, wrapper } from './MixedBreedPicker.css'

type Props = {
  breeds: Array<Breed>
  defaultSelectedBreeds?: Array<Breed>
  onBreedsChange?: (selectedBreeds: Array<Breed>) => void
  loading?: boolean
  name?: string
}

export const MixedBreedPicker = ({ breeds, defaultSelectedBreeds, onBreedsChange, loading, name }: Props) => {
  const [selectedBreeds, setSelectedBreeds] = useState<Array<Breed>>(defaultSelectedBreeds ?? [])
  const { t } = useTranslation('purchase-form')

  const handleDelete = (id: Breed['id']) => () => {
    const newSelectedBreeds = selectedBreeds.filter((breed) => breed.id !== id)
    setSelectedBreeds(newSelectedBreeds)
    onBreedsChange?.(newSelectedBreeds)
  }

  const handleAdd = (breed: Breed | null) => {
    if (breed) {
      const newSelectedBreeds = [...selectedBreeds, breed]
      setSelectedBreeds(newSelectedBreeds)
      onBreedsChange?.(newSelectedBreeds)
    }
  }

  const availableBreeds = useMemo(() => {
    return breeds.filter((breed) => !selectedBreeds.includes(breed))
  }, [breeds, selectedBreeds])

  // 'Vet inte' on its own is a valid answer, otherwise a mix needs at least two breeds
  const hasCompleteSelection =
    selectedBreeds.length > 1 || selectedBreeds.some((breed) => breed.id === UNKNOWN_BREED_ID)

  return (
    <div className={wrapper}>
      <div className={content}>
        <Text size="xs" color="textSecondary">
          {t('FIELD_MIXED_BREEDS_LABEL')}
        </Text>

        {selectedBreeds.length > 0 && (
          <>
            <ul className={yStack({ gap: 'xs' })}>
              {selectedBreeds.map((breed) => (
                <li key={breed.id} className={xStack({ justifyContent: 'space-between' })}>
                  <Text size="md">{breed.displayName}</Text>
                  <button className={deleteButton} type="button" onClick={handleDelete(breed.id)} disabled={loading}>
                    {}
                    <CrossIconSmall />
                  </button>
                </li>
              ))}
            </ul>
            {selectedBreeds.map((breed) => (
              <input key={breed.id} type="hidden" name={name} value={breed.id} />
            ))}
          </>
        )}
      </div>

      <Combobox
        className={combobox}
        items={availableBreeds}
        displayValue={(breed) => breed.displayName}
        selectedItem={null}
        onSelectedItemChange={handleAdd}
        placeholder={t('FIELD_MIXED_BREEDS_PLACEHOLDER')}
        noMatchesMessage={t('FIELD_BREEDS_NO_OPTIONS')}
        mutliSelect={true}
        disabled={loading}
        required={!hasCompleteSelection}
        size="small"
      />
    </div>
  )
}
